import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, Table, Dropdown, number } from 'yankees-react-utils';
import { useOktaQuery, Advance, Player } from '../../../data';
import useConfig from '../../../utils/useConfig';
import ReportCard from '../../../components/ReportCard';
import ReportNote from '../../../components/ReportNote';
import ThrowingArms from './ThrowingArms';

const ThrowingArmsRunTimes = ({ match: { params: { teamId } } }) => {
    const { t: translate } = useTranslation();
    const { CurrentYear } = useConfig();
    const lastYear = CurrentYear - 1;
    const noteYearOpts = new Array(CurrentYear - 2012).fill().map((_, idx) => ({ name: CurrentYear - idx }));

    const [noteYear, setNoteYear] = useState(CurrentYear);
    const { data, isLoading } = useOktaQuery(['teamRoster', teamId, 'batter'], (t) => Player.list(t, { teamId, position: 'batter' }));
    const { data: oart, isLoading: isReportLoading } = useOktaQuery(
        ['oart', teamId, CurrentYear],
        (t) => Advance.oart(t, { teamId, startYear: lastYear, endYear: CurrentYear }),
    );

    const pickAttCurrentYear = oart?.pickatt.find((pa) => pa.yearid === CurrentYear);
    const pickAttLastYear = oart?.pickatt.find((pa) => pa.yearid === lastYear);
    const pitchOutsCurrentYear = oart?.pitchouts.filter((pa) => pa.yearid === CurrentYear);
    const pitchOutsLastYear = oart?.pitchouts.filter((pa) => pa.yearid === lastYear);

    const lineup = data?.sort((p1, p2) => p1.FormalName.localeCompare(p2.FormalName));
    const throwingArmsSections = [
        { title: translate('sections.opponent.team.catchers'), position: 'C', players: lineup?.filter((p) => p.PrimaryPosition === 2) },
        { title: translate('sections.opponent.team.infielders'), position: 'IF', players: lineup?.filter((p) => p.PrimaryPosition >= 3 && p.PrimaryPosition < 7) },
        { title: translate('sections.opponent.team.outfielders'), position: 'OF', players: lineup?.filter((p) => p.PrimaryPosition >= 7 && p.PrimaryPosition < 10) },
        { title: translate('sections.opponent.team.other'), players: lineup?.filter((p) => p.PrimaryPosition < 2 || p.PrimaryPosition > 9) },
    ].filter((x) => x.players?.length);

    return (
        <div>
            {(isLoading || isReportLoading) && <LoadingSpinner />}
            <ReportCard title={translate('sections.opponent.team.notes')}>
                <Table hover={false} responsive={false}>
                    <tbody>
                        <tr>
                            <th>{translate('sections.opponent.team.team-notes')}</th>
                            <td rowSpan={3}>
                                <ReportNote teamId={teamId} yearId={noteYear} type="oart" section={2} />
                            </td>
                            <th>{translate('common.year')}</th>
                            <th className="text-nowrap">{translate('sections.opponent.team.abbr-pickoff-2b')}</th>
                            <th>{translate('sections.opponent.team.pitchouts')}</th>
                        </tr>
                        <tr>
                            <th rowSpan={2}>
                                <Dropdown options={noteYearOpts} value={noteYear} onSelect={(_, key) => setNoteYear(Number(key))} />
                            </th>
                            <td>{CurrentYear}</td>
                            <td>{number(pickAttCurrentYear?.picks2b, 0)}</td>
                            <td>
                                {!pitchOutsCurrentYear?.length && '--'}
                                {pitchOutsCurrentYear?.map((po) => (
                                    <div key={`${po.balls}-${po.strikes}`}>{po.balls}-{po.strikes}: {po.pitchouts}</div>
                                ))}
                            </td>
                        </tr>
                        <tr>
                            <td>{lastYear}</td>
                            <td>{number(pickAttLastYear?.picks2b, 0)}</td>
                            <td>
                                {!pitchOutsLastYear?.length && '--'}
                                {pitchOutsLastYear?.map((po) => (
                                    <div key={`${po.balls}-${po.strikes}`}>{po.balls}-{po.strikes}: {po.pitchouts}</div>
                                ))}
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </ReportCard>
            {throwingArmsSections.map((x) => (
                <ReportCard key={x.title} title={x.title}>
                    <Table striped={false}>
                        <tbody>
                            {x.players.map((p) => <ThrowingArms key={p.PlayerID} year={CurrentYear} player={p} position={x.position} />)}
                        </tbody>
                    </Table>
                </ReportCard>
            ))}
        </div>
    );
};

ThrowingArmsRunTimes.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        }).isRequired,
    }).isRequired,
};

export default ThrowingArmsRunTimes;
